import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import InfoIcon from "@mui/icons-material/Info";
import { Box, Button, ButtonGroup, Stack, Tooltip, Typography } from "@mui/material";
import { ShowContextProvider, toggleListItemExpand, useDeleteWithConfirmController, useRecordContext, useShowContext } from "ra-core";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { ClassificationRuleDto, ClassificationRuleInput, RuleRowState } from "@/gen/api/securefile";
import { ClassificationRuleEdit } from "./ClassificationRuleEdit";
import { useHasPermit } from "@/authProvider/useHasPermit";
import { EDIT_DATA_CLASSIFICATION_ENCRYPTION, MANAGE_DATA_CATALOG } from "@/constants";
import CreateOrEdit from "@/controller/CreateOrEdit";
import WithSave from "@/controller/WithSave";
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers";
import { ClassificationRulesControllerProps } from "./ClassificationRulesContext";
import Confirm from '../widgets/Confirm';
import { LocalMessageLocation, useLocalMessagingRoot } from '@/contexts/LocalMessagingRoot';
import { useLmStatesPropagate } from '@/contexts/LocalMessageHookProtocols/useLmStatesPropagate';
import { useLmGlobalDisabledMsg } from '@/contexts/LocalMessageHookProtocols/useLmGlobalDisabledMsg';


const clssRulesRes = 'settings/securefile/classification-rules'
const ruleRevertRes = 'secure-files-clss-rule-revert'

const RowExpandClassificationRuleEdit: FC<{ctrls: ClassificationRulesControllerProps}> = ({ctrls}) => {
  const ctxRecord : ClassificationRuleDto = useRecordContext() as any
  const { changePendingIds, allRules, refetch } = ctrls
  const [states, setStates] = useState({
    showOldVersion: false,
    oldVerRecord: 
      ctxRecord.rowStatus === RuleRowState.PreAdd && changePendingIds.has(ctxRecord.predecessorId!)
      ? allRules.find(x => x.ruleId === ctxRecord.predecessorId)
      : undefined,
  })
  const showCtxValue = useMemo(() => {
    return states.showOldVersion && !! states.oldVerRecord
      ? { record: states.oldVerRecord, refetch }
      : { record: ctxRecord, refetch }
  }, [ctxRecord, refetch, states])
  const {toggleVersion} = useMemo(() => ({
    toggleVersion: () => setStates(v => ({...v, showOldVersion: !v.showOldVersion})),
  }), [])

  return (
    <ShowContextProvider value={showCtxValue as any}>
      <RowExpandClassificationRuleEditBody currentId={ctxRecord.ruleId!} versionOption={states} toggleVersion={toggleVersion} />
    </ShowContextProvider>
  )
}

const RowExpandClassificationRuleEditBody: FC<{
  currentId: number;
  versionOption: {
    showOldVersion: boolean;
    oldVerRecord: ClassificationRuleDto | undefined;
  },
  toggleVersion: () => void,
}> = ({currentId, versionOption, toggleVersion}) => {
  const record = useRecordContext()
  const dto: ClassificationRuleDto = record as any
  const dispatch = useDispatch()
  const { refetch } = useShowContext()
  const hasClssPermit = useHasPermit([EDIT_DATA_CLASSIFICATION_ENCRYPTION])
  const hasEditPermit = useHasPermit([MANAGE_DATA_CATALOG])
  
  const closeExpanded = () => {
    dispatch(toggleListItemExpand('settings/securefile/classification-rules', currentId))
  }

  const dialogRef = useRef<HTMLElement|null>(null)
  useEffect(() => {
    setTimeout(() => {
      dialogRef.current?.focus()
    })
  }, [])

  const [states, setStates] = useState({
    dirty: false,
    globalDisabled: dto && (
      dto.rowStatus !== RuleRowState.Normal &&
      dto.rowStatus !== RuleRowState.PreAdd)
  })
  const authority = useCallback(() => Promise.resolve(states.globalDisabled), [states.globalDisabled])
  
  const {LocalMessagingRootHost, subscribe, publish} = useLocalMessagingRoot()
  const {assembleSubStates} = useLmStatesPropagate({ 
    key: 'root',
    asUpperStage: {
      dirtyStateInformed: {
        handler() {
          return (_, dirty) => setStates(v => ({...v, dirty }))
        },
        deps: []
      }
    }
  }, {subscribe, publish})
  const  {sendDownDisabledMsg} = useLmGlobalDisabledMsg({ authority }, {subscribe, publish})

  useEffect(() => {
    const globalDisabled = dto && (
      dto.rowStatus !== RuleRowState.Normal &&
      dto.rowStatus !== RuleRowState.PreAdd)
    setStates(v => ({...v, globalDisabled, 
      dirty: false // we know "sendDownDisabledMsg" will not change
    }))
    sendDownDisabledMsg(globalDisabled)
  }, [dto, sendDownDisabledMsg])

  const handlers = useDefaultSavedHandlers("Update Classification Rule")
  const revertHandlers = useDefaultSavedHandlers("Revert Classification Rule")
  refetch && revertHandlers.onSuccess.merge(refetch)

  const handleSave = (save: (record:any,callbacks?:any)=>void) => async () => {
    if (!hasClssPermit && !hasEditPermit) { return }
    if (!states.dirty) { return }

    try {
      const payload: ClassificationRuleInput = (await assembleSubStates(['editor'])).editor
      payload.ruleId = dto.ruleId
      
      save(payload, {
        onSuccess: () => {
          refetch && refetch()
          closeExpanded()
        }
      })
    } catch (error) {
      // validation must had failed
    }
  }

  const delHandlers = useDefaultSavedHandlers('Delete File Classification Rule')
  delHandlers.onSuccess.merge(useCallback(() => {
    refetch && refetch()
  }, [refetch]))

  const {
    open: delConfirmOpen,
    handleDialogOpen,
    handleDialogClose,
    handleDelete,
    loading: delSaving,
  } = useDeleteWithConfirmController({
    resource: clssRulesRes,
    record,
    mutationMode: 'pessimistic',
    ...delHandlers,
  });

  const setRuleToBeDeleted = useCallback((r:ClassificationRuleDto) => {
    handleDialogOpen({stopPropagation:()=>{}} as any)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Box role="dialog">
      <span tabIndex={-1} ref={dialogRef} className="sr-only">Field Edit Region for {`${dto.displayName}`}</span>
      <Box sx={{ mb: 2, px: 4, }}>
        <LocalMessagingRootHost>
          <LocalMessageLocation id="editor">
            <ClassificationRuleEdit />
          </LocalMessageLocation>
        </LocalMessagingRootHost>
      </Box>
      <Stack direction="row" sx={{
        mb: -2, mx: -2, py: '10px', px: 5,
        borderTop: theme => `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.default',
        '& > button': { width: 96, mr: 2, },
      }}>
        {(hasClssPermit || hasEditPermit) && dto.rowStatus !== RuleRowState.PreDelete &&
          <CreateOrEdit record={record} id={record.id} resource="settings/securefile/classification-rules" basePath="" {...handlers}>
            <WithSave 
              render={({save, saving}) =>
                <Button
                  disabled={!states.dirty || states.globalDisabled || saving}
                  variant="contained" 
                  size="small" 
                  color="primary"
                  onClick={handleSave(save)}
                >
                  Save
                </Button>
              }
            />
          </CreateOrEdit>}
        <Button
          variant="outlined" 
          size="small" 
          color="primary"
          onClick={closeExpanded}
        >
          Cancel
        </Button>
        <Box sx={{ mx: 'auto' }} />
        {versionOption.oldVerRecord &&
          <ButtonGroup aria-label="toggle between versions" size="small" disableElevation variant="text" color="secondary" sx={{ mr: 4, '& button': { border: 'unset !important' } }}>
            <Tooltip title="Show Old Version">
              <Button disabled={versionOption.showOldVersion} onClick={() => {
                if (!states.dirty || window.confirm('Discard edits and show old version ?')) {
                  toggleVersion()
                }
              }}>
                <ArrowLeftIcon />
              </Button>
            </Tooltip>
            <Tooltip title="Show Current Changes">
              <Button disabled={!versionOption.showOldVersion} onClick={toggleVersion}><ArrowRightIcon /></Button>
            </Tooltip>
          </ButtonGroup>}
        {((dto.rowStatus === RuleRowState.PreAdd && !!dto.predecessorId) || dto.rowStatus === RuleRowState.PreDelete || dto.rowStatus === RuleRowState.ChangePending) &&
          <CreateOrEdit resource={ruleRevertRes} basePath="" {...revertHandlers} id={dto.ruleId} record={dto}>
            <WithSave render={({save}) => (
              <Button variant="contained" size="small" color="warning" disabled={versionOption.showOldVersion}
                onClick={() => save(dto.ruleId)}
              >
                Revert
              </Button>)}
            />
          </CreateOrEdit>}
        {(dto.rowStatus === RuleRowState.Normal || (dto.rowStatus === RuleRowState.PreAdd && !dto.predecessorId)) &&
          <Button variant="contained" size="small" color="error"
            onClick={() => setRuleToBeDeleted(dto)}
          >
            Delete
          </Button>}
      </Stack>
      <Confirm
        isOpen={delConfirmOpen}
        loading={delSaving}
        title={"Delete File Classification Rule"}
        onConfirm={handleDelete}
        onClose={(e:any) => {
          handleDialogClose(e)
        }}
        content={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <InfoIcon fontSize="large" sx={{ color: 'info.main' }}/>
            <Typography
              component="span"
              variant="body2"
              sx={{ ml: 1 }}
            >
              Please confirm whether to proceed with the deletion of this
              {` "${record?.displayName}" `}
              File Classification Rule.
            </Typography>
          </Box>
        }
      />
    </Box>
  )
}

export default RowExpandClassificationRuleEdit;

